import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Archive, RotateCcw, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import ProjectHeader from "../components/ProjectHeader";
import { useAuth } from "../context/AuthContext";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { formatRelativeTime, entryThumbSrc } from "../lib/media";
import { Button } from "../components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";

/**
 * Iter-85 — Unassigned Archive view. Reachable from UnassignedPage.
 *
 * - Shows soft-deleted entries (archived = true).
 * - Per-row select, "Select all" / "Clear" controls.
 * - Bulk "Permanently delete" with strong confirmation; warns when
 *   any selected entry's media is still referenced by an active
 *   (non-archived) entry — permanent delete cascades the media when
 *   it's the LAST reference.
 * - Per-row Restore button to push it back into the active queue.
 */
export default function UnassignedArchivePage() {
    const { projectId } = useParams();
    const { user } = useAuth();
    const routeBase = useRouteBase();
    const apiBase = useApiBase();

    const [project, setProject] = useState(null);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    const canManage = isOwner || isAdmin;

    const load = async () => {
        try {
            const [{ data: p }, { data }] = await Promise.all([
                api.get(`${apiBase}/${projectId}`),
                api.get(`${apiBase}/${projectId}/state-entries`, {
                    params: { assigned: false, archived: true },
                }),
            ]);
            setProject(p);
            setEntries(data);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const toggle = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Only entries the current user can act on (created by them OR
    // they're project owner). The select-all should respect this so
    // we never queue an entry for delete that the server will refuse.
    const actionable = useMemo(
        () => entries.filter((e) => canManage || e.created_by === user?.id),
        [entries, canManage, user?.id]
    );

    const allSelected =
        actionable.length > 0 && selectedIds.size === actionable.length;

    const selectAll = () => {
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(actionable.map((e) => e.id)));
        }
    };

    const restore = async (entry) => {
        try {
            await api.post(
                `${apiBase}/${projectId}/state-entries/${entry.id}/unarchive`
            );
            setEntries((prev) => prev.filter((e) => e.id !== entry.id));
            setSelectedIds((prev) => {
                const n = new Set(prev);
                n.delete(entry.id);
                return n;
            });
            toast.success("Restored to Unassigned queue");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const sharedSelectedCount = useMemo(() => {
        let c = 0;
        for (const e of entries) {
            if (selectedIds.has(e.id) && e.media_shared) c += 1;
        }
        return c;
    }, [entries, selectedIds]);

    const confirmDelete = async () => {
        if (selectedIds.size === 0) return;
        setDeleting(true);
        const ids = Array.from(selectedIds);
        let okCount = 0;
        let failCount = 0;
        for (const id of ids) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await api.delete(`${apiBase}/${projectId}/state-entries/${id}`);
                okCount += 1;
            } catch {
                failCount += 1;
            }
        }
        setEntries((prev) => prev.filter((e) => !selectedIds.has(e.id)));
        setSelectedIds(new Set());
        setDeleting(false);
        setConfirmOpen(false);
        if (failCount === 0) {
            toast.success(`Permanently deleted ${okCount} image${okCount === 1 ? "" : "s"}`);
        } else {
            toast.error(
                `Deleted ${okCount}; ${failCount} could not be deleted`
            );
        }
    };

    return (
        <div data-testid="unassigned-archive-page" className="min-h-screen">
            <ProjectHeader
                projectName="Unassigned Archive"
                subtitle="Soft-deleted images"
            />
            <main className="max-w-2xl mx-auto px-5 pt-6 pb-48">
                <Link
                    data-testid="unassigned-archive-back"
                    to={`${routeBase}/${projectId}/unassigned`}
                    className="tap inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-5"
                >
                    <ArrowLeft size={14} />
                    Back to Unassigned
                </Link>

                <div className="mb-6 flex items-center gap-3">
                    <div className="h-12 w-12 rounded-lg bg-amber-400/15 text-amber-300 flex items-center justify-center">
                        <Archive size={22} />
                    </div>
                    <div>
                        <h1 className="font-display text-3xl font-black text-white leading-none">
                            Archive
                        </h1>
                        <p className="text-xs text-neutral-400 mt-1">
                            Restore back to Unassigned, or permanently delete.
                        </p>
                    </div>
                </div>

                {error ? (
                    <p
                        data-testid="unassigned-archive-error"
                        className="text-sm text-red-400 mb-4"
                    >
                        {error}
                    </p>
                ) : null}

                {loading ? (
                    <ul
                        className="grid grid-cols-3 gap-3"
                        data-testid="unassigned-archive-loading"
                    >
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <li
                                key={i}
                                className="aspect-square rounded-xl bg-card border border-white/10 animate-pulse"
                            />
                        ))}
                    </ul>
                ) : entries.length === 0 ? (
                    <div
                        data-testid="unassigned-archive-empty"
                        className="rounded-xl border border-dashed border-white/10 p-8 text-center"
                    >
                        <p className="text-sm text-neutral-400">
                            Archive is empty. Soft-deleted Unassigned images
                            will appear here.
                        </p>
                    </div>
                ) : (
                    <>
                        <div
                            data-testid="unassigned-archive-action-bar"
                            className="sticky top-2 z-10 flex items-center gap-2 rounded-xl border border-white/10 bg-card/80 backdrop-blur px-3 py-2 mb-4"
                        >
                            <Button
                                type="button"
                                data-testid="unassigned-archive-select-all"
                                variant="outline"
                                onClick={selectAll}
                                className="h-9 text-xs border-white/10"
                                disabled={actionable.length === 0}
                            >
                                {allSelected ? "Clear" : "Select all"}
                            </Button>
                            <span
                                data-testid="unassigned-archive-selected-count"
                                className="text-[11px] text-neutral-400"
                            >
                                {selectedIds.size} selected
                            </span>
                            <div className="flex-1" />
                            <Button
                                type="button"
                                data-testid="unassigned-archive-delete-btn"
                                onClick={() => setConfirmOpen(true)}
                                disabled={selectedIds.size === 0 || deleting}
                                className="h-9 px-3 text-xs bg-red-600 text-white hover:bg-red-500 font-semibold"
                            >
                                <Trash2 size={12} className="mr-1.5" />
                                Permanently delete
                            </Button>
                        </div>

                        <ul
                            data-testid="unassigned-archive-list"
                            className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                        >
                            {entries.map((e) => {
                                const canAct =
                                    canManage || e.created_by === user?.id;
                                const isSelected = selectedIds.has(e.id);
                                return (
                                    <li
                                        key={e.id}
                                        data-testid={`unassigned-archive-card-${e.id}`}
                                        className={`relative rounded-xl overflow-hidden border bg-card transition-colors ${
                                            isSelected
                                                ? "border-red-500/60 ring-2 ring-red-500/40"
                                                : "border-white/10"
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            data-testid={`unassigned-archive-toggle-${e.id}`}
                                            onClick={() => canAct && toggle(e.id)}
                                            disabled={!canAct}
                                            className="tap block w-full text-left"
                                        >
                                            <img
                                                src={entryThumbSrc(projectId, e)}
                                                alt="archived"
                                                className="w-full aspect-square object-cover opacity-70"
                                            />
                                            <div className="p-2">
                                                <div className="text-[10px] text-neutral-400 truncate">
                                                    {e.created_by_name || "Someone"}
                                                </div>
                                                <div className="text-[10px] text-neutral-500 truncate">
                                                    archived{" "}
                                                    {formatRelativeTime(
                                                        e.archived_at ||
                                                            e.created_at
                                                    )}
                                                </div>
                                                {e.media_shared ? (
                                                    <div
                                                        data-testid={`unassigned-archive-shared-${e.id}`}
                                                        className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] text-amber-200 bg-amber-400/15 border border-amber-400/30 rounded-full px-1.5 py-0.5"
                                                    >
                                                        <AlertTriangle
                                                            size={9}
                                                        />
                                                        Shared
                                                    </div>
                                                ) : null}
                                            </div>
                                        </button>
                                        {canAct ? (
                                            <button
                                                type="button"
                                                data-testid={`unassigned-archive-restore-${e.id}`}
                                                onClick={() => restore(e)}
                                                title="Restore"
                                                aria-label="Restore"
                                                className="tap absolute top-2 right-2 h-7 w-7 rounded-md bg-black/60 text-neutral-300 hover:text-emerald-200 inline-flex items-center justify-center"
                                            >
                                                <RotateCcw size={12} />
                                            </button>
                                        ) : null}
                                        {isSelected ? (
                                            <span
                                                data-testid={`unassigned-archive-selected-${e.id}`}
                                                className="absolute top-2 left-2 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-red-500 text-white"
                                            >
                                                Selected
                                            </span>
                                        ) : null}
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                )}
            </main>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="unassigned-archive-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-red-400">
                            Permanently delete{" "}
                            {selectedIds.size === 1
                                ? "this image"
                                : `${selectedIds.size} selected images`}
                            ?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This cannot be undone.
                            {sharedSelectedCount > 0 ? (
                                <span
                                    data-testid="unassigned-archive-delete-shared-warning"
                                    className="mt-3 flex items-start gap-2 rounded-md border border-amber-400/30 bg-amber-400/10 text-amber-100 p-2 text-xs"
                                >
                                    <AlertTriangle
                                        size={13}
                                        className="text-amber-300 mt-0.5 shrink-0"
                                    />
                                    <span>
                                        {sharedSelectedCount} of these{" "}
                                        {sharedSelectedCount === 1
                                            ? "image is"
                                            : "images are"}{" "}
                                        also assigned to a scene or character
                                        elsewhere. Deleting will only remove{" "}
                                        the archived copy — the assigned copy
                                        is left untouched. The underlying photo
                                        is preserved as long as any reference
                                        remains.
                                    </span>
                                </span>
                            ) : null}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="unassigned-archive-delete-confirm-cancel"
                            disabled={deleting}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="unassigned-archive-delete-confirm-go"
                            onClick={confirmDelete}
                            disabled={deleting}
                            className="bg-red-600 text-white hover:bg-red-500 font-semibold"
                        >
                            {deleting ? "Deleting…" : "Permanently delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
